import React, { useContext, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { media } from 'context';
import { track } from 'lib/analytics';
import { Color, IconName } from 'shared';

import Button from './Button';
import Icon from './Icon';
import IconButton, { Props as IconButtonProps } from './IconButton';
import Layout from './Layout';
import Text from './Text';
import { DS_CLASS_NAME, SCROLLABLE_BOX_SHADOW } from './constants';
import { useOnClickOutside } from './helpers';
import { ButtonSize, ButtonType } from './types';

type IconButtonType = IconButtonProps['type'];
type IconButtonSize = IconButtonProps['size'];

export interface MultiAction {
  label: string;
  onClick: VoidFunction;
  icon?: IconName;
  iconColor?: Color;
  disabled?: boolean;
}

interface Props {
  actions: MultiAction[];
  desktopLabel?: string;
  desktopButtonType?: ButtonType;
  desktopButtonSize?: ButtonSize;
  desktopIconName?: IconName;
  mobileIconName?: IconName;
  mobileIconType?: IconButtonType;
  mobileIconSize?: IconButtonSize;
  context?: string;
  align?: 'left' | 'right';
  disabled?: boolean;
  className?: string;
}

const MultiActionButton: React.FC<Props> = ({
  actions,
  desktopLabel = '+ Add',
  desktopButtonType = 'primary',
  desktopButtonSize = 'xxs',
  desktopIconName = 'plus',
  mobileIconName = 'plus',
  mobileIconType = 'noBackground',
  mobileIconSize = 'lg',
  context,
  align = 'right',
  disabled = false,
  className
}) => {
  const { isMobile } = useContext(media);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasActions = useMemo(() => actions && actions.length > 0, [actions]);

  useOnClickOutside(containerRef, isMenuOpen ? () => setIsMenuOpen(false) : null);

  const toggleMenu = () => {
    if (disabled || !hasActions) {
      return;
    }
    setIsMenuOpen(prev => !prev);
    track('Element Interacted', {
      context,
      name: 'MultiActionButton',
      type: 'button',
      value: isMenuOpen ? 'close' : 'open'
    });
  };

  const handleActionClick = (action: MultiAction) => {
    if (action.disabled) {
      return;
    }
    setIsMenuOpen(false);
    track('Element Interacted', {
      context,
      name: action.label,
      type: 'button',
      value: 'multiAction'
    });
    action.onClick();
  };

  const menuAlignmentClass = align === 'left' ? 'is-align-left' : 'is-align-right';

  return (
    <Layout
      __ref={containerRef}
      position="relative"
      __className={classNames('MultiActionButton', DS_CLASS_NAME, className, {
        'is-open': isMenuOpen,
        'is-disabled': disabled || !hasActions
      })}
    >
      {isMobile ? (
        <IconButton
          size={mobileIconSize}
          type={mobileIconType}
          name={mobileIconName}
          disabled={disabled || !hasActions}
          onClick={toggleMenu}
        />
      ) : (
        <Button
          type={desktopButtonType}
          size={desktopButtonSize}
          text={desktopLabel}
          icon={desktopIconName}
          iconPosition="left"
          onClick={toggleMenu}
          disabled={disabled || !hasActions}
        />
      )}
      {isMenuOpen && hasActions ? (
        <Layout
          __className={classNames('MultiActionButton-menu', menuAlignmentClass)}
          position="absolute"
          top={isMobile ? 52 : 48}
          color="white"
          borderRadius={12}
          boxShadow={SCROLLABLE_BOX_SHADOW}
          paddingY={4}
        >
          {actions.map((action, index) => (
            <button
              key={`${action.label}-${index}`}
              className="MultiActionButton-menuItem"
              type="button"
              disabled={action.disabled}
              onClick={() => handleActionClick(action)}
            >
              <Layout align="center" gap={8} paddingY={10} paddingX={16}>
                {action.icon ? <Icon name={action.icon} size="sm" color={action.iconColor} /> : null}
                <Text size="body-md" color={action.disabled ? 'gray-400' : 'gray-900'} semibold={!action.disabled}>
                  {action.label}
                </Text>
              </Layout>
            </button>
          ))}
        </Layout>
      ) : null}
    </Layout>
  );
};

export default MultiActionButton;
